const ACTIVITY_STORAGE_KEY_PREFIX = 'hrv4.activities';
const LEGACY_ACTIVITY_STORAGE_KEY = 'hrv4.activities';
const ACTIVITY_EVENT_NAME = 'hrv4:activities-updated';



export function getLocalDateString(date = new Date()) {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function getLocalTimeString(date = new Date()) {
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

export function normalizeActivityDate(value) {
  if (!value || typeof value !== 'string') return getLocalDateString();

  const trimmed = value.trim();
  if (!trimmed) return getLocalDateString();

  // Hızlı yol: zaten YYYY-MM-DD ise dokunma.
  const dateOnly = trimmed.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) return dateOnly;

  const parsed = new Date(trimmed);
  if (!isNaN(parsed.getTime())) return getLocalDateString(parsed);

  return getLocalDateString();
}

export function normalizeActivityTime(value, fallback = '00:00') {
  if (!value || typeof value !== 'string') return fallback;

  const trimmed = value.trim();
  const match = trimmed.match(/^(\d{1,2}):(\d{2})/);
  if (match) {
    const hour = Number(match[1]);
    const minute = Number(match[2]);
    if (hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
      return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    }
  }

  const parsed = new Date(trimmed);
  if (!isNaN(parsed.getTime())) return getLocalTimeString(parsed);

  return fallback;
}

// ─── ID NORMALIZATION ────────────────────────────────────────────

function normalizeUserId(value) {
  const id = String(value || '').trim();

  if (!id || id === 'null' || id === 'undefined') {
    return null;
  }

  return id;
}

function readUserIdFromJsonStorage(key) {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(key) || window.sessionStorage.getItem(key);
    if (!raw) return null;

    const parsed = JSON.parse(raw);

    return (
      normalizeUserId(parsed?.id) ||
      normalizeUserId(parsed?.userId) ||
      normalizeUserId(parsed?._id) ||
      normalizeUserId(parsed?.email)
    );
  } catch {
    return null;
  }
}

export function getCurrentActivityUserId() {
  if (typeof window === 'undefined') return null;

  return (
    normalizeUserId(window.localStorage.getItem('userId')) ||
    normalizeUserId(window.localStorage.getItem('currentUserId')) ||
    normalizeUserId(window.sessionStorage.getItem('userId')) ||
    normalizeUserId(window.sessionStorage.getItem('currentUserId')) ||
    readUserIdFromJsonStorage('user') ||
    readUserIdFromJsonStorage('currentUser') ||
    readUserIdFromJsonStorage('authUser')
  );
}

export function getActivityStorageKey(userId = getCurrentActivityUserId()) {
  const normalizedUserId = normalizeUserId(userId);

  if (!normalizedUserId) {
    return null;
  }

  return `${ACTIVITY_STORAGE_KEY_PREFIX}:${normalizedUserId}`;
}

// ─── ACTIVITY NORMALIZATION ──────────────────────────────────────

export function normalizeActivity(activity = {}) {
  return {
    id: String(activity.id || activity.measurementId || Date.now()),
    // Lokal tarih fallback'ı; eskiden `toISOString()` UTC gün veriyordu.
    date: normalizeActivityDate(activity.date),
    time: normalizeActivityTime(activity.time, '00:00'),
    type: activity.type || activity.name || 'Unknown',
    category: activity.category || 'other',
    durationMin: Number(activity.durationMin || 0),
    intensity: activity.intensity || 'Moderate',
    calories: Number(activity.calories || 0),
    note: activity.note || '',
    synced: activity.synced !== false,
  };
}

function emitActivityUpdate(userId = getCurrentActivityUserId()) {
  if (typeof window === 'undefined') return;

  window.dispatchEvent(
    new CustomEvent(ACTIVITY_EVENT_NAME, {
      detail: {
        userId: normalizeUserId(userId),
      },
    }),
  );
}

export function subscribeToActivityUpdates(callback) {
  if (typeof window === 'undefined') return () => {};

  window.addEventListener(ACTIVITY_EVENT_NAME, callback);

  return () => window.removeEventListener(ACTIVITY_EVENT_NAME, callback);
}

export function clearLegacyActivitiesCache() {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.removeItem(LEGACY_ACTIVITY_STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
}

export function clearAllActivityCaches() {
  if (typeof window === 'undefined') return;

  try {
    Object.keys(window.localStorage)
      .filter((key) => key === LEGACY_ACTIVITY_STORAGE_KEY || key.startsWith(`${ACTIVITY_STORAGE_KEY_PREFIX}:`))
      .forEach((key) => window.localStorage.removeItem(key));

    emitActivityUpdate(null);
  } catch {
    // Ignore storage failures.
  }
}

export function loadStoredActivities(userId = getCurrentActivityUserId()) {
  if (typeof window === 'undefined') return [];

  const storageKey = getActivityStorageKey(userId);

  if (!storageKey) {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(storageKey);

    if (!raw) return [];

    const parsed = JSON.parse(raw);

    if (!Array.isArray(parsed)) return [];

    return parsed.map(normalizeActivity);
  } catch {
    return [];
  }
}

export function storeActivities(activities, userId = getCurrentActivityUserId()) {
  if (typeof window === 'undefined') return;

  const storageKey = getActivityStorageKey(userId);

  if (!storageKey) {
    return;
  }

  try {
    clearLegacyActivitiesCache();

    window.localStorage.setItem(
      storageKey,
      JSON.stringify((activities || []).map(normalizeActivity)),
    );

    emitActivityUpdate(userId);
  } catch {
    // Ignore storage quota failures.
  }
}

export function clearStoredActivities(userId = getCurrentActivityUserId()) {
  if (typeof window === 'undefined') return;

  const storageKey = getActivityStorageKey(userId);

  if (!storageKey) {
    return;
  }

  try {
    window.localStorage.removeItem(storageKey);
    emitActivityUpdate(userId);
  } catch {
    // Ignore storage failures.
  }
}

export function getRecentActivities(limit = 2, userId = getCurrentActivityUserId()) {
  return loadStoredActivities(userId)
    .sort((a, b) =>
      (b.date || '').localeCompare(a.date || '') ||
      (b.time || '').localeCompare(a.time || ''),
    )
    .slice(0, limit);
}